import React, { useContext, useEffect, useId, useRef } from 'react'

import { Modal } from '@inno-ui/modal'
import { Popover, PopoverPlacements } from '@inno-ui/popover'
import { useTranslation } from 'react-i18next'
import { UserContext } from 'shared/contexts'
import { useUserCompanies } from 'shared/hooks/useUserCompanies'

import { USER_PROFILE_Z_INDEX } from '../UserProfile.constants'
import { UserProfileTrigger } from '../components/UserProfileTrigger'
import { useUserProfileLayers } from '../hooks/useUserProfileLayers'
import styles from './UserProfilePopover.module.scss'
import type { UserProfilePopoverProps } from './UserProfilePopover.types'

const UserProfilePopover: React.FC<UserProfilePopoverProps> = ({
  isOpen,
  setIsOpen
}) => {
  const { t } = useTranslation()
  const { user } = useContext(UserContext)
  const { selectedCompany } = useUserCompanies()
  const id = useId()
  const overlayRef = useRef<HTMLDivElement>(null)
  const triggerButtonRef = useRef<HTMLButtonElement>(null)
  const editPreferencesButtonRef = useRef<HTMLButtonElement>(null)
  const { layers, layerIndex, setLayerIndex } = useUserProfileLayers({
    isOpen,
    setIsOpen,
    editPreferencesButtonRef,
    includeBodyPaddings: false
  })

  const isMenuOpen = isOpen && layerIndex === 0
  const isPreferencesOpen = isOpen && layerIndex > 0

  useEffect(() => {
    if (!isMenuOpen) return
    const overlay = overlayRef.current
    const trigger = triggerButtonRef.current
    const frame = requestAnimationFrame(() => {
      editPreferencesButtonRef.current?.focus()
    })
    return () => {
      cancelAnimationFrame(frame)
      if (
        overlay?.contains(document.activeElement) ||
        document.activeElement === document.body
      ) {
        trigger?.focus()
      }
    }
  }, [isMenuOpen])

  return (
    <>
      <Popover
        isOpen={isMenuOpen}
        setIsOpen={setIsOpen}
        placement={PopoverPlacements.BottomEnd}
        offset={8}
        zIndex={USER_PROFILE_Z_INDEX.surface}
        data-user-profile-popover='true'
        closeOnEscape
        triggerWrapperClassName={styles.trigger}
        childrenClassName={styles.trigger}
        userMaxHeight='calc(100dvh - 24px)'
        overlay={
          <div
            id={id}
            ref={overlayRef}
            className={styles.popover}
            role='dialog'
            aria-label={t('user_profile.my_profile')}
          >
            {layers[0].body}
          </div>
        }
      >
        <UserProfileTrigger
          buttonRef={triggerButtonRef}
          isOpen={isMenuOpen}
          popoverId={id}
          userName={user?.name}
          userAvatar={user?.profile?.avatar}
          company={selectedCompany}
          placeholderText={t('navbar.userMenu.userPlaceholder')}
        />
      </Popover>
      <Modal
        zIndex={USER_PROFILE_Z_INDEX.surface}
        isOpen={isPreferencesOpen}
        onClose={() => setIsOpen(false)}
        maxWidth={558}
        maxHeight='min(800px, calc(100dvh - 40px))'
        noWrapperPadding
        withHeaderLine={false}
        layerIndex={Math.max(0, layerIndex - 1)}
        setLayerIndex={index => setLayerIndex(index + 1)}
        layers={layers.slice(1)}
      />
    </>
  )
}

export { UserProfilePopover }
